// Typed fetch wrapper for the SkillPatch API.

import type {
  Profile,
  FollowUser,
  SocialLinks,
  RatingsSummary,
  Skill,
  SkillDetail,
  ApiKey,
  ApiKeyCreated,
  Access,
  ConflictResolutionFile,
  Dashboard,
  GithubConflictDetail,
  GithubRepo,
  GithubStatus,
  Grant,
  SkillFile,
  Suggestion,
  SuggestionDetail,
  SuggestionDiff,
  Team,
  TeamDetail,
  TeamInvite,
  TeamRole,
  TeamSkill,
  Thread,
  ThreadCategory,
  ThreadDetail,
  ThreadStatus,
  Notification,
  Version,
  VersionDetail,
} from "./types";

// Single-container setup: frontend and API are same-origin, so relative URLs
// work everywhere (local Docker on :8000, prod on HTTPS). Override with
// REACT_APP_API_BASE_URL only if you need to point at a different host.
export const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

export async function apiFetch(
  path: string,
  opts: RequestInit = {},
  accessToken?: string | null,
): Promise<Response> {
  const headers = new Headers(opts.headers);
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  if (opts.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(`${API_BASE}${path}`, { ...opts, headers });
}

// Fetch + parse JSON; throws ApiError on non-2xx.
// Pull a human-readable message from a FastAPI error body.
// 4xx with a string `detail` → that string; 422 → `detail` is an array of {msg,...}.
function errorMessage(data: unknown, res: Response): string {
  const body = data as { detail?: unknown; message?: unknown } | null;
  const detail = body?.detail ?? body?.message;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: unknown };
    if (typeof first?.msg === "string") {
      return first.msg.replace(/^value error,\s*/i, "");
    }
  }
  return res.statusText || "Request failed";
}

// Carries the HTTP status alongside the server's message so callers can present errors
// precisely (e.g. map a GitHub 403 to a "permissions" notice). `message` stays the raw
// server detail, so existing `e.message` usages keep working unchanged.
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export async function apiJson<T>(
  path: string,
  opts: RequestInit = {},
  accessToken?: string | null,
): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(path, opts, accessToken);
  } catch {
    // Network-level failure (offline, DNS, CORS) — no HTTP status at all.
    throw new ApiError("Couldn’t reach the server. Check your connection and try again.", 0);
  }
  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(errorMessage(data, res), res.status);
  }
  return data as T;
}

// --- Skills / versions / ratings (Phase 4) ---
export function listSkills(
  params: { search?: string; tag?: string; collection?: string; layer?: number; owner?: string; limit?: number } = {},
  token?: string | null,
) {
  const q = new URLSearchParams();
  if (params.search) q.set("search", params.search);
  if (params.tag) q.set("tag", params.tag);
  if (params.collection) q.set("collection", params.collection);
  if (params.layer !== undefined) q.set("layer", String(params.layer));
  if (params.owner) q.set("owner", params.owner);
  if (params.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  // Pass the token when signed in so the server marks which results the viewer has
  // bookmarked; anonymous (no token) requests still work and just come back un-bookmarked.
  return apiJson<Skill[]>(`/skills${qs ? `?${qs}` : ""}`, {}, token);
}

export function getSkill(slug: string, token?: string | null) {
  return apiJson<SkillDetail>(`/skills/${slug}`, {}, token);
}

// Creator claim: begin the GitHub-verified consent/takedown flow for an imported (Layer 0) skill.
// Anonymous — no token. `all` applies the action to every skill under the same GitHub owner.
// `maintain` (consent only) also takes ownership after the claimer logs in / signs up.
// Returns the GitHub authorize URL the browser should navigate to.
export function startConsent(
  slug: string,
  intent: "consent" | "takedown",
  all = false,
  maintain = false,
) {
  const q = new URLSearchParams({ intent });
  if (all) q.set("all", "true");
  if (maintain) q.set("maintain", "true");
  return apiJson<{ authorize_url: string }>(`/skills/${slug}/consent/start?${q.toString()}`);
}

export type MaintainResult = {
  owned: string[];
  already_owned: string[];
  skipped: string[];
  failed_count: number;
};

// Redeem the maintain grant (referenced by an httpOnly cookie set during the claim callback) as the
// logged-in user → transfer ownership of the GitHub-verified skills to this account. The grant id is
// never in the URL; it rides the same-origin cookie, so no argument is needed here.
export function redeemMaintain(token: string) {
  return apiJson<MaintainResult>("/claim/maintain", { method: "POST" }, token);
}

export type ClaimResultEntry = { slug: string; name: string; repo?: string | null; reason?: string | null };
export type ClaimResultSummary = {
  intent: "consent" | "takedown" | string;
  owner: string;
  login: string;
  succeeded: ClaimResultEntry[];
  failed: ClaimResultEntry[];
};

// Fetch a bulk-claim summary (which skills succeeded / failed) by id.
export function getClaimResult(id: string) {
  return apiJson<ClaimResultSummary>(`/claim/results/${id}`);
}

export function getVersions(slug: string, token?: string | null) {
  return apiJson<Version[]>(`/skills/${slug}/versions`, {}, token);
}

export function getSkillFiles(slug: string, token?: string | null, versionUid?: string | null) {
  const qs = versionUid ? `?version_uid=${versionUid}` : "";
  return apiJson<SkillFile[]>(`/skills/${slug}/files${qs}`, {}, token);
}

export function getVersion(slug: string, uid: string, token?: string | null) {
  return apiJson<VersionDetail>(`/skills/${slug}/versions/${uid}`, {}, token);
}

export function restoreVersion(slug: string, uid: string, token: string) {
  return apiJson<Version>(`/skills/${slug}/versions/${uid}/restore`, { method: "POST" }, token);
}

export function getRatings(slug: string, token?: string | null) {
  return apiJson<RatingsSummary>(`/skills/${slug}/ratings`, {}, token);
}

export function rateSkill(
  slug: string,
  body: { stars: number; comment?: string },
  token: string,
) {
  return apiJson<RatingsSummary>(
    `/skills/${slug}/ratings`,
    { method: "POST", body: JSON.stringify(body) },
    token,
  );
}

// --- Discussion threads ---
export function listThreads(
  slug: string,
  params: { category?: ThreadCategory; sort?: "unanswered_first" | "newest" } = {},
  token?: string | null,
) {
  const q = new URLSearchParams();
  if (params.category) q.set("category", params.category);
  if (params.sort) q.set("sort", params.sort);
  const qs = q.toString();
  return apiJson<Thread[]>(`/skills/${slug}/discussions${qs ? `?${qs}` : ""}`, {}, token);
}

export function getThread(slug: string, threadId: string, token?: string | null) {
  return apiJson<ThreadDetail>(`/skills/${slug}/discussions/${threadId}`, {}, token);
}

export function createThread(
  slug: string,
  body: { category: ThreadCategory; title: string; body: string },
  token: string,
) {
  return apiJson<Thread>(
    `/skills/${slug}/discussions`,
    { method: "POST", body: JSON.stringify(body) },
    token,
  );
}

export function replyToThread(
  slug: string,
  threadId: string,
  body: string,
  token: string,
  parentReplyId?: string | null,
) {
  return apiJson<ThreadDetail>(
    `/skills/${slug}/discussions/${threadId}/replies`,
    { method: "POST", body: JSON.stringify({ body, parent_reply_id: parentReplyId ?? null }) },
    token,
  );
}

export function setThreadStatus(slug: string, threadId: string, status: ThreadStatus, token: string) {
  return apiJson<Thread>(
    `/skills/${slug}/discussions/${threadId}/status`,
    { method: "POST", body: JSON.stringify({ status }) },
    token,
  );
}

export function deleteThread(slug: string, threadId: string, token: string) {
  return apiJson<Thread>(`/skills/${slug}/discussions/${threadId}`, { method: "DELETE" }, token);
}

export function deleteReply(slug: string, threadId: string, replyId: string, token: string) {
  return apiJson<ThreadDetail>(
    `/skills/${slug}/discussions/${threadId}/replies/${replyId}`,
    { method: "DELETE" },
    token,
  );
}

// --- Notifications ---
export function listNotifications(
  params: { unreadOnly?: boolean; limit?: number; before?: string } = {},
  token: string,
) {
  const q = new URLSearchParams();
  if (params.unreadOnly) q.set("unread_only", "true");
  if (params.limit) q.set("limit", String(params.limit));
  if (params.before) q.set("before", params.before);
  const qs = q.toString();
  return apiJson<Notification[]>(`/notifications${qs ? `?${qs}` : ""}`, {}, token);
}

export function getUnreadCount(token: string) {
  return apiJson<{ count: number }>("/notifications/unread-count", {}, token);
}

export function markNotificationRead(id: string, token: string) {
  return apiJson<Notification>(`/notifications/${id}/read`, { method: "POST" }, token);
}

export function markAllNotificationsRead(token: string) {
  return apiJson<{ ok: boolean }>("/notifications/read-all", { method: "POST" }, token);
}

// Toggle a bookmark. POST saves, DELETE removes; returns the resulting saved state.
export function setBookmark(slug: string, bookmarked: boolean, token: string) {
  return apiJson<{ bookmarked: boolean }>(
    `/skills/${slug}/bookmark`,
    { method: bookmarked ? "POST" : "DELETE" },
    token,
  );
}

// Hide / unhide an admin-curated default skill from the caller's own library (and machine).
export function setDefaultHidden(slug: string, hidden: boolean, token: string) {
  return apiJson<{ default_hidden: boolean }>(
    `/skills/${slug}/default-hide`,
    { method: hidden ? "POST" : "DELETE" },
    token,
  );
}

export function setPublished(slug: string, publish: boolean, token: string) {
  return apiJson<Skill>(`/skills/${slug}/${publish ? "publish" : "unpublish"}`, { method: "POST" }, token);
}

export function deleteSkill(slug: string, token: string) {
  return apiJson<void>(`/skills/${slug}`, { method: "DELETE" }, token);
}

export function getSharedSkills(token: string) {
  return apiJson<Skill[]>("/skills/shared", {}, token);
}

export function mySkills(token: string, search?: string) {
  const qs = search ? `?search=${encodeURIComponent(search)}` : "";
  return apiJson<Skill[]>(`/skills/mine${qs}`, {}, token);
}

// --- Per-skill access grants (owner shares to teams/users) ---
export function listGrants(slug: string, token: string) {
  return apiJson<Grant[]>(`/skills/${slug}/grants`, {}, token);
}
export function shareWithTeam(slug: string, teamSlug: string, access: Access, token: string) {
  return apiJson<Grant[]>(`/skills/${slug}/grants/team/${teamSlug}`, { method: "PUT", body: JSON.stringify({ access }) }, token);
}
export function shareWithUser(slug: string, email: string, access: Access, token: string) {
  return apiJson<Grant[]>(`/skills/${slug}/grants/user`, { method: "PUT", body: JSON.stringify({ email, access }) }, token);
}
export function revokeGrant(slug: string, kind: "team" | "user", id: string, token: string) {
  return apiJson<Grant[]>(`/skills/${slug}/grants/${kind}/${id}`, { method: "DELETE" }, token);
}

// --- Teams ---
export function listTeams(token: string) {
  return apiJson<Team[]>("/teams", {}, token);
}
export function getTeam(slug: string, token: string) {
  return apiJson<TeamDetail>(`/teams/${slug}`, {}, token);
}
// Skills shared with a team (team-page view). Adding a skill still goes through the owner-only
// shareWithTeam grant endpoint; changing access on / removing an already-shared skill is open to
// the skill owner OR a team admin via these team-scoped endpoints.
export function listTeamSkills(slug: string, token: string) {
  return apiJson<TeamSkill[]>(`/teams/${slug}/skills`, {}, token);
}
export function setTeamSkillAccess(slug: string, skillSlug: string, access: Access, token: string) {
  return apiJson<TeamSkill[]>(`/teams/${slug}/skills/${skillSlug}`, { method: "PUT", body: JSON.stringify({ access }) }, token);
}
export function removeTeamSkill(slug: string, skillSlug: string, token: string) {
  return apiJson<TeamSkill[]>(`/teams/${slug}/skills/${skillSlug}`, { method: "DELETE" }, token);
}
export function createTeam(name: string, token: string) {
  return apiJson<Team>("/teams", { method: "POST", body: JSON.stringify({ name }) }, token);
}
export function renameTeam(slug: string, name: string, token: string) {
  return apiJson<TeamDetail>(`/teams/${slug}`, { method: "PATCH", body: JSON.stringify({ name }) }, token);
}
export function listInvites(token: string) {
  return apiJson<TeamInvite[]>("/teams/invites", {}, token);
}
export function inviteMember(slug: string, email: string, role: TeamRole, token: string) {
  return apiJson<TeamInvite>(`/teams/${slug}/invites`, { method: "POST", body: JSON.stringify({ user_email: email, role }) }, token);
}
export function setMemberRole(slug: string, userId: string, role: TeamRole, token: string) {
  return apiJson<TeamDetail>(`/teams/${slug}/members/${userId}/role`, { method: "POST", body: JSON.stringify({ role }) }, token);
}
export function removeMember(slug: string, userId: string, token: string) {
  return apiJson<TeamDetail>(`/teams/${slug}/members/${userId}`, { method: "DELETE" }, token);
}
// Pending-invite management (owner/admin): change the role it will grant, or cancel it outright.
export function changeInviteRole(slug: string, inviteId: string, role: TeamRole, token: string) {
  return apiJson<TeamDetail>(`/teams/${slug}/invites/${inviteId}/role`, { method: "POST", body: JSON.stringify({ role }) }, token);
}
export function revokeInvite(slug: string, inviteId: string, token: string) {
  return apiJson<TeamDetail>(`/teams/${slug}/invites/${inviteId}`, { method: "DELETE" }, token);
}
export function deleteTeam(slug: string, token: string) {
  return apiJson<null>(`/teams/${slug}`, { method: "DELETE" }, token);
}
export function acceptInvite(id: string, token: string) {
  return apiJson<TeamInvite>(`/teams/invites/${id}/accept`, { method: "POST" }, token);
}
export function declineInvite(id: string, token: string) {
  return apiJson<TeamInvite>(`/teams/invites/${id}/decline`, { method: "POST" }, token);
}

// --- Dashboard + API keys (Phase 7) ---
export function getDashboard(token: string) {
  return apiJson<Dashboard>("/users/me/dashboard", {}, token);
}
export function listApiKeys(token: string) {
  return apiJson<ApiKey[]>("/api-keys", {}, token);
}
export function createApiKey(name: string, token: string) {
  return apiJson<ApiKeyCreated>("/api-keys", { method: "POST", body: JSON.stringify({ name }) }, token);
}
export async function revokeApiKey(id: string, token: string) {
  const res = await fetch(`${API_BASE}/api-keys/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to revoke key");
}

export function downloadUrl(slug: string) {
  return `${API_BASE}/skills/${slug}/download`;
}

// --- Suggestions (Phase 5) ---

const API_KEY_STORAGE = "sp_api_key";

export function getStoredApiKey(): string | null {
  return typeof window !== "undefined" ? window.localStorage.getItem(API_KEY_STORAGE) : null;
}

// Ensure the logged-in user has a usable API key — uses the account's default key.
export async function ensureApiKey(token: string): Promise<string> {
  const existing = getStoredApiKey();
  if (existing) return existing;
  const k = await getDefaultApiKey(token);
  window.localStorage.setItem(API_KEY_STORAGE, k.key);
  return k.key;
}

export function getDefaultApiKey(token: string) {
  return apiJson<ApiKeyCreated>("/api-keys/default", {}, token);
}
export function regenerateDefaultKey(token: string) {
  return apiJson<ApiKeyCreated>("/api-keys/default/regenerate", { method: "POST" }, token);
}
// Subscribers notified whenever the stored default key changes (e.g. Settings → Regenerate), so
// any on-screen setup command can refresh in place instead of waiting for a reload.
type ApiKeyListener = (key: string) => void;
const apiKeyListeners = new Set<ApiKeyListener>();

export function subscribeApiKey(cb: ApiKeyListener): () => void {
  apiKeyListeners.add(cb);
  return () => { apiKeyListeners.delete(cb); };
}

export function setStoredApiKey(key: string) {
  if (typeof window !== "undefined") window.localStorage.setItem(API_KEY_STORAGE, key);
  apiKeyListeners.forEach((cb) => cb(key));
}

function postSuggestion(slug: string, title: string, message: string, token: string, file?: File | null) {
  const form = new FormData();
  form.append("title", title);
  form.append("message", message);
  // A logged-in user submits with their session (Bearer), so the backend tags it source=user —
  // even with a tar attached. Only agents authenticating with an API key are source=agent.
  if (file) form.append("file", file);
  return fetch(`${API_BASE}/skills/${slug}/suggestions`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
}

export async function createSuggestion(
  slug: string,
  title: string,
  message: string,
  token: string,
  file?: File | null,
): Promise<Suggestion> {
  const res = await postSuggestion(slug, title, message, token, file);
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = (data && (data.detail || data.message)) || res.statusText;
    throw new Error(typeof detail === "string" ? detail : "Failed to submit");
  }
  return data as Suggestion;
}

export function listSuggestions(slug: string, token: string) {
  return apiJson<Suggestion[]>(`/skills/${slug}/suggestions`, {}, token);
}

export function getSuggestion(id: string, token: string) {
  return apiJson<SuggestionDetail>(`/suggestions/${id}`, {}, token);
}

export function getSuggestionDiff(id: string, token: string) {
  return apiJson<SuggestionDiff>(`/suggestions/${id}/diff`, {}, token);
}

// Owner actions on a suggestion.
export function processSuggestion(id: string, token: string) {
  return apiJson<Suggestion>(`/suggestions/${id}/process`, { method: "POST" }, token);
}
export function approveSuggestion(id: string, token: string) {
  return apiJson<Suggestion>(`/suggestions/${id}/approve`, { method: "POST" }, token);
}
export function rejectSuggestion(id: string, token: string) {
  return apiJson<Suggestion>(`/suggestions/${id}/reject`, { method: "POST" }, token);
}

export async function createSkill(
  file: File,
  visibility: string,
  token: string,
  tags: string[] = [],
  domains: string[] = [],
  teamSlug?: string,
): Promise<SkillDetail> {
  const form = new FormData();
  form.append("file", file);
  form.append("visibility", visibility);
  for (const t of tags) form.append("tags", t);
  for (const d of domains) form.append("domains", d);
  if (teamSlug) form.append("team_slug", teamSlug);
  const res = await fetch(`${API_BASE}/skills`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = (data && (data.detail || data.message)) || res.statusText;
    throw new Error(typeof detail === "string" ? detail : "Upload failed");
  }
  return data as SkillDetail;
}

export function listForks(slug: string) {
  return apiJson<Skill[]>(`/skills/${slug}/forks`);
}

export async function forkSkill(slug: string, token: string): Promise<SkillDetail> {
  const res = await fetch(`${API_BASE}/skills/${slug}/fork`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = (data && (data.detail || data.message)) || res.statusText;
    throw new Error(typeof detail === "string" ? detail : "Fork failed");
  }
  return data as SkillDetail;
}

export async function updateSkillSettings(
  slug: string,
  settings: { accept_suggestions?: boolean; discussions_enabled?: boolean; fork_intent?: string },
  token: string,
): Promise<SkillDetail> {
  const res = await fetch(`${API_BASE}/skills/${slug}/settings`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(settings),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = (data && (data.detail || data.message)) || res.statusText;
    throw new Error(typeof detail === "string" ? detail : "Update failed");
  }
  return data as SkillDetail;
}

// --- GitHub integration (Phase 1) ---

// Account-level connection.
export function getGithubStatus(token: string) {
  return apiJson<GithubStatus>("/github/status", {}, token);
}
// Begin the OAuth "Connect with GitHub" flow: returns the GitHub authorize URL the browser should
// navigate to. GitHub then redirects back to /github/oauth/callback, which lands on /settings.
export function startGithubOAuth(token: string) {
  return apiJson<{ authorize_url: string }>("/github/oauth/start", {}, token);
}
export function disconnectGithub(token: string) {
  return apiJson<void>("/github/disconnect", { method: "DELETE" }, token);
}
export function listGithubRepos(token: string) {
  return apiJson<GithubRepo[]>("/github/repos", {}, token);
}
export function listGithubBranches(fullName: string, token: string) {
  return apiJson<string[]>(`/github/repos/${fullName}/branches`, {}, token);
}

// Import a repo subtree as a new skill (returns the pending skill — poll like an upload).
export function importFromGithub(
  body: {
    repo: string;
    branch: string;
    subpath?: string;
    visibility?: string;
    tags?: string[];
    domains?: string[];
    team_slug?: string;
  },
  token: string,
) {
  return apiJson<SkillDetail>(
    "/github/import",
    { method: "POST", body: JSON.stringify(body) },
    token,
  );
}

// Per-skill link / unlink / push.
export function linkSkillGithub(
  slug: string,
  body: { repo: string; branch: string; subpath?: string; auto_push?: boolean },
  token: string,
) {
  return apiJson<SkillDetail>(
    `/skills/${slug}/github`,
    { method: "PUT", body: JSON.stringify(body) },
    token,
  );
}
export function updateGithubSettings(slug: string, autoPush: boolean, token: string) {
  return apiJson<SkillDetail>(
    `/skills/${slug}/github`,
    { method: "PATCH", body: JSON.stringify({ auto_push: autoPush }) },
    token,
  );
}
export function unlinkSkillGithub(slug: string, token: string) {
  return apiJson<SkillDetail>(`/skills/${slug}/github`, { method: "DELETE" }, token);
}
export function pushSkillGithub(slug: string, token: string) {
  return apiJson<SkillDetail>(`/skills/${slug}/github/push`, { method: "POST" }, token);
}
export function resyncSkillGithub(slug: string, token: string) {
  return apiJson<SkillDetail>(`/skills/${slug}/github/resync`, { method: "POST" }, token);
}

// Conflict resolution (Phase 4).
export function getGithubConflict(slug: string, token: string) {
  return apiJson<GithubConflictDetail>(`/skills/${slug}/github/conflict`, {}, token);
}
export function resolveGithubConflict(
  slug: string,
  files: ConflictResolutionFile[],
  headSha: string,
  token: string,
) {
  return apiJson<{ suggestion_id: string }>(
    `/skills/${slug}/github/resolve`,
    { method: "POST", body: JSON.stringify({ files, head_sha: headSha }) },
    token,
  );
}

// --- Social share ---

export type ShareContentIn = {
  why?: string;
  how?: string;
  audience?: string;
  mode: "manual" | "generate";
  custom_text?: string;
};

export type ShareContentOut = {
  x_post: string;
  linkedin_post: string;
  skill_url: string;
};

export function getShareDraft(slug: string, token: string): Promise<ShareContentOut | null> {
  return apiJson<ShareContentOut | null>(`/skills/${slug}/share-draft`, {}, token);
}

export function generateShareContent(
  slug: string,
  body: ShareContentIn,
  token?: string | null,
): Promise<ShareContentOut> {
  return apiJson<ShareContentOut>(
    `/skills/${slug}/share-content`,
    { method: "POST", body: JSON.stringify(body) },
    token,
  );
}

// --- Password reset (OTP by email) ---
// Request a reset code. Always resolves (the backend returns a generic message whether or not
// the email is registered), so the UI can advance to the code-entry step regardless.
export function forgotPassword(email: string) {
  return apiJson<{ message: string }>("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

// Submit the emailed code + a new password. Throws ApiError on a bad/expired code.
export function resetPassword(email: string, otp: string, password: string) {
  return apiJson<{ message: string }>("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ email, otp, password }),
  });
}

export function submitFeedback(
  body: { message: string; type: string },
  token: string,
): Promise<{ ok: boolean }> {
  return apiJson<{ ok: boolean }>(
    "/feedback",
    { method: "POST", body: JSON.stringify(body) },
    token,
  );
}

// Public "Contact us" form — stored server-side for admins (no auth required).
export function submitContact(
  body: { name: string; email: string; company: string; message: string },
): Promise<{ ok: boolean }> {
  return apiJson<{ ok: boolean }>(
    "/contact",
    { method: "POST", body: JSON.stringify(body) },
  );
}

// --- Admin ---

export type AdminStats = {
  users_total: number;
  skills_total: number;
  skills_pending: number;
  suggestions_total: number;
  feedback_total: number;
  feedback_unresolved: number;
  contact_total: number;
  contact_unresolved: number;
  viewer_roles: string[];
};

export type AdminUser = {
  id: string;
  email: string;
  display_name: string;
  username: string | null;
  roles: string[];
  created_at: string;
};

export type AdminFeedback = {
  id: string;
  email: string;
  message: string;
  type: string;
  resolved: boolean;
  created_at: string;
};

export type AdminContact = {
  id: string;
  name: string;
  email: string;
  company: string;
  message: string;
  resolved: boolean;
  created_at: string;
};

export type AdminSkill = {
  id: string;
  slug: string;
  name: string;
  summary: string;
  status: string;
  layer: number;
  visibility?: string;
  is_default?: boolean;
  owner_can_own_defaults?: boolean; // owner is admin / admin:write — required for a default
  owner_id: string;
  owner_name: string;
  owner_email: string;
  created_at: string;
  updated_at: string;
};

export type AdminUserDetail = AdminUser & {
  skills: { slug: string; name: string; status: string; created_at: string }[];
  suggestions_count: number;
  cost_usd: number;
  agent_runs: number;
  last_active: string | null;
};

export const adminApi = {
  stats: (token: string) =>
    apiJson<AdminStats>("/admin/stats", {}, token),

  listUsers: (token: string) =>
    apiJson<AdminUser[]>("/admin/users", {}, token),

  getUser: (userId: string, token: string) =>
    apiJson<AdminUserDetail>(`/admin/users/${userId}`, {}, token),

  grantRole: (userId: string, role: string, token: string) =>
    apiJson<{ ok: boolean }>(`/admin/users/${userId}/roles/${role}`, { method: "POST" }, token),

  revokeRole: (userId: string, role: string, token: string) =>
    apiJson<{ ok: boolean }>(`/admin/users/${userId}/roles/${role}`, { method: "DELETE" }, token),

  listFeedback: (token: string) =>
    apiJson<AdminFeedback[]>("/admin/feedback", {}, token),

  resolveFeedback: (id: string, token: string) =>
    apiJson<{ ok: boolean }>(`/admin/feedback/${id}/resolve`, { method: "PATCH" }, token),

  unresolveFeedback: (id: string, token: string) =>
    apiJson<{ ok: boolean }>(`/admin/feedback/${id}/unresolve`, { method: "PATCH" }, token),

  listContact: (token: string) =>
    apiJson<AdminContact[]>("/admin/contact", {}, token),

  resolveContact: (id: string, token: string) =>
    apiJson<{ ok: boolean }>(`/admin/contact/${id}/resolve`, { method: "PATCH" }, token),

  unresolveContact: (id: string, token: string) =>
    apiJson<{ ok: boolean }>(`/admin/contact/${id}/unresolve`, { method: "PATCH" }, token),

  listSkills: (token: string) =>
    apiJson<AdminSkill[]>("/admin/skills", {}, token),

  unpublishSkill: (slug: string, token: string) =>
    apiJson<{ ok: boolean }>(`/admin/skills/${slug}`, { method: "DELETE" }, token),

  // Add / remove a skill from the default set every user gets (admin:write).
  setDefaultSkill: (slug: string, isDefault: boolean, token: string) =>
    apiJson<{ ok: boolean; is_default: boolean }>(
      `/admin/skills/${slug}/default`,
      { method: isDefault ? "PUT" : "DELETE" },
      token,
    ),

  getCosts: (token: string) =>
    apiJson<AdminCosts>("/admin/costs", {}, token),
};

export type CostRow = {
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  runs: number;
};

export type AdminCosts = {
  total: CostRow;
  by_agent: (CostRow & { agent: string })[];
  by_user: (CostRow & { user_id: string; email: string; display_name: string })[];
};

// [profiles+follows] Public profiles + follow API.
export function getProfile(username: string, token?: string | null) {
  return apiJson<Profile>(`/users/${encodeURIComponent(username)}`, {}, token);
}
export function updateProfile(
  patch: Partial<{ display_name: string; username: string; bio: string | null; social_links: SocialLinks; show_email: boolean }>,
  token: string,
) {
  return apiJson<Profile>("/users/me/profile", { method: "PATCH", body: JSON.stringify(patch) }, token);
}
export function checkUsername(u: string, token?: string) {
  return apiJson<{ available: boolean; reason: string | null; normalized: string }>(
    `/username-available?u=${encodeURIComponent(u)}`, {}, token,
  );
}
export function followUser(username: string, token: string) {
  return apiJson<{ following: boolean; followers: number }>(
    `/users/${encodeURIComponent(username)}/follow`, { method: "POST" }, token,
  );
}
export function unfollowUser(username: string, token: string) {
  return apiJson<{ following: boolean; followers: number }>(
    `/users/${encodeURIComponent(username)}/follow`, { method: "DELETE" }, token,
  );
}
function followListQs(cursor?: string | null, search?: string | null) {
  const q = new URLSearchParams();
  if (cursor) q.set("cursor", cursor);
  if (search) q.set("search", search);
  const s = q.toString();
  return s ? `?${s}` : "";
}
export function getFollowers(username: string, cursor?: string | null, token?: string | null, search?: string | null) {
  return apiJson<{ items: FollowUser[]; next_cursor: string | null }>(
    `/users/${encodeURIComponent(username)}/followers${followListQs(cursor, search)}`, {}, token,
  );
}
export function getFollowing(username: string, cursor?: string | null, token?: string | null, search?: string | null) {
  return apiJson<{ items: FollowUser[]; next_cursor: string | null }>(
    `/users/${encodeURIComponent(username)}/following${followListQs(cursor, search)}`, {}, token,
  );
}
// A user's published (active + public) skills — powers the profile's Published-skills list.
export function getUserSkills(username: string, token?: string | null) {
  return apiJson<Skill[]>(`/users/${encodeURIComponent(username)}/skills`, {}, token);
}
// Home → Following feed: recent skills from people you follow (last N days, newest first).
export function getFollowingFeed(token: string, days = 7) {
  return apiJson<Skill[]>(`/feed/following?days=${days}`, {}, token);
}
// People search — matches display name / username (or username only when usernameOnly, for "@handle").
export function searchUsers(q: string, limit = 6, token?: string | null, usernameOnly = false) {
  const extra = usernameOnly ? "&username_only=true" : "";
  return apiJson<FollowUser[]>(`/search/users?q=${encodeURIComponent(q)}&limit=${limit}${extra}`, {}, token);
}
